import { useMutation } from '@apollo/client';

import type { CreateReceiptInput, Transaction } from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { logEvent } from 'features/Analytics/analytics';

import { RefetchTransactionReceiptsDocument } from '../graphql/refetchTransactionReceipts.gql';
import { CreateTransactionReceiptDocument } from './graphql/createTransactionReceipt.gql';

interface Params {
  transactionId: string;
  transactionType: Transaction['type'];
  transactionTotal: number;
}

const useCreateTransactionReceiptMutation = ({
  transactionId,
  transactionTotal,
  transactionType,
}: Params) => {
  const { companyProfileId } = useCompanyContext();

  if (!companyProfileId) {
    throw new Error(
      'companyProfileId is undefined in useCreateReceiptMutation',
    );
  }

  const [mutation, hook] = useMutation(CreateTransactionReceiptDocument, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: RefetchTransactionReceiptsDocument,
        variables: { transactionId },
      },
    ],
  });

  const createTransactionReceipt = (
    input: Omit<CreateReceiptInput, 'companyProfileId' | 'transactionId'>,
  ) =>
    mutation({
      onCompleted: (data) => {
        logEvent({
          name: 'Transaction Receipt Uploaded',
          properties: {
            receiptId: data.createReceipt.receipt?.receiptId,
            source: 'web-app',
            transactionId,
            transactionType,
            type: 'External',
          },
          usePrefix: false,
        });
      },
      variables: {
        input: {
          ...input,
          companyProfileId,
          total: transactionTotal,
          transactionId,
        },
      },
    });

  return [createTransactionReceipt, hook] as const;
};

export default useCreateTransactionReceiptMutation;
